import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { flattenNodes } from '@/lib/node-tree';
import type { NodesResponse } from '@/stores/scope';

/**
 * `/talent/placement-constraints` (人・組織機能 増分③ SC-07、D-607。増分⑤で SC-08 タブを追加)。
 *
 * 三軸 (制限次元/継続性/解除手段) × 四値 (判定) の配置制約の登録・一覧 (E11) と、
 * 通勤プロファイル (E12) をまとめて扱う。単発の希望休を入力できるフォームは出さない (§11.4)。
 *
 * ⚠️ `GET /employees` は company_admin のみ有効 (LeaveGrantsPage.tsx と同じ制約)。own_store の
 * 店長はこれを取得できないため、失敗時は致命扱いにせず「対象者を検索できません」に留める。
 * その場合でも `employee_id` を省略した `GET /placement-constraints` は self を既定値として返すため、
 * 自分自身の配置制約 (view:self) は選択肢に関わらず確認できる。
 */

type Employee = { employee_id: string; employee_number: string; family_name: string; given_name: string; status: string };
const employeeLabel = (e: Employee) => `${e.family_name} ${e.given_name} (${e.employee_number})`;

const useEmployeeOptions = () => useQuery<Employee[]>({
  queryKey: ['employees'], queryFn: () => apiFetch<Employee[]>('/employees'), retry: false,
});

type Constraint = {
  constraint_id: string; employee_id: string | null; external_person_id: string | null;
  dimension: 'time' | 'place' | 'task'; sub_dimension: string; detail_key: string; value_json: string;
  continuity: 'permanent' | 'period' | 'recurring'; release_method: 'money' | 'ops' | 'consent' | 'none';
  release_unit_price: number | null; release_note: string | null;
  judgment: 'ok' | 'conditional' | 'ng' | 'unconfirmed'; target_node_id: string | null;
  effective_from: string | null; effective_to: string | null; recurrence_json: string | null;
  confirmed_at: string | null; confirmed_by: string | null; review_due_at: string | null;
  row_version: number;
};

const DIMENSION_LABELS: Record<Constraint['dimension'], string> = { time: '時間', place: '場所', task: '業務' };
const CONTINUITY_LABELS: Record<Constraint['continuity'], string> = { permanent: '恒久', period: '期間限定', recurring: '定期' };
const RELEASE_METHOD_LABELS: Record<Constraint['release_method'], string> = { money: '金銭', ops: '運用調整', consent: '本人同意', none: 'なし' };
const JUDGMENT_LABELS: Record<Constraint['judgment'], string> = { ok: '可', conditional: '条件付き可', ng: '不可', unconfirmed: '未確認' };
const JUDGMENT_BADGE_CLASS: Record<Constraint['judgment'], string> = {
  ok: 'bg-success-bg text-success-text border-success-border',
  conditional: 'bg-alert-bg text-alert-text border-alert-border',
  ng: 'bg-danger-bg text-danger-text border-danger-border',
  unconfirmed: 'bg-surface-sunken text-text-sub border-surface-border',
};

type ConstraintDraft = {
  dimension: Constraint['dimension']; subDimension: string; detailKey: string; valueJson: string;
  continuity: Constraint['continuity']; releaseMethod: Constraint['release_method'];
  effectiveFrom: string; effectiveTo: string;
};

const emptyDraft: ConstraintDraft = {
  dimension: 'time', subDimension: '', detailKey: '', valueJson: '',
  continuity: 'permanent', releaseMethod: 'none', effectiveFrom: '', effectiveTo: '',
};

function ConstraintsTab() {
  const qc = useQueryClient();
  const { data: employees = [], isLoading: employeesLoading, isError: employeesError } = useEmployeeOptions();
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [draft, setDraft] = useState<ConstraintDraft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Constraint | null>(null);
  const [judgmentTarget, setJudgmentTarget] = useState<Constraint | null>(null);

  // 対象者ピッカーが使える (= 一覧を取得できた) のに未選択のままでは、誰の制約か決まらないため
  // 問い合わせない (「自分自身」が既定に見えるが、company_admin 等は自分の employee_id を
  // 持たないため self フォールバックが 400 になる)。ピッカー自体が使えない (`GET /employees` が
  // company_admin 限定のため 403、LeaveGrantsPage.tsx と同じ制約) 場合のみ self 既定に頼る。
  // ⚠️ 一覧取得が完了する前 (isLoading) は「使えない」と誤判定して self フォールバックへ
  // 一瞬フォールバックしてしまうため、読み込み完了までは問い合わせ自体を保留する。
  const canPickEmployee = !employeesError && employees.length > 0;
  const effectiveEmployeeId = selectedEmployeeId || undefined;
  const shouldQuery = !employeesLoading && (!!effectiveEmployeeId || !canPickEmployee);
  const { data: constraints = [], isLoading, isError, error } = useQuery<Constraint[]>({
    queryKey: ['placement-constraints', effectiveEmployeeId],
    queryFn: () => apiFetch(`/placement-constraints${effectiveEmployeeId ? `?employee_id=${effectiveEmployeeId}` : ''}`),
    enabled: shouldQuery,
  });
  const { data: profile } = useQuery<{ employee_id: string; commute_type: string | null }>({
    queryKey: ['placement-profile', effectiveEmployeeId],
    queryFn: () => apiFetch(`/placement-constraints/profile/${effectiveEmployeeId}`),
    enabled: !!effectiveEmployeeId,
  });

  const employeeOptionsLabel = useMemo(() => new Map(employees.map((e) => [e.employee_id, employeeLabel(e)])), [employees]);

  const createMut = useMutation({
    mutationFn: (d: ConstraintDraft) => apiFetch('/placement-constraints', {
      method: 'POST',
      body: JSON.stringify({
        employee_id: effectiveEmployeeId,
        dimension: d.dimension, sub_dimension: d.subDimension.trim(), detail_key: d.detailKey.trim(), value_json: d.valueJson.trim(),
        continuity: d.continuity, release_method: d.releaseMethod,
        effective_from: d.continuity === 'period' ? d.effectiveFrom : null,
        effective_to: d.continuity === 'period' ? d.effectiveTo : null,
      }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['placement-constraints', effectiveEmployeeId] });
      setDraft(null); toast.success('登録しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '登録に失敗しました'),
  });

  const judgmentMut = useMutation({
    mutationFn: ({ constraint, judgment }: { constraint: Constraint; judgment: Constraint['judgment'] }) =>
      apiFetch(`/placement-constraints/${constraint.constraint_id}`, {
        method: 'PUT', body: JSON.stringify({ judgment, row_version: constraint.row_version }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['placement-constraints', effectiveEmployeeId] });
      setJudgmentTarget(null); toast.success('更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/placement-constraints/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['placement-constraints', effectiveEmployeeId] });
      setDeleteTarget(null); toast.success('削除しました');
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleteTarget(null); },
  });

  const profileMut = useMutation({
    mutationFn: (commuteType: string | null) => apiFetch(`/placement-constraints/profile/${effectiveEmployeeId}`, {
      method: 'PUT', body: JSON.stringify({ commute_type: commuteType }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['placement-profile', effectiveEmployeeId] });
      toast.success('通勤情報を更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const canSaveDraft = !!draft && draft.subDimension.trim() !== '' && draft.detailKey.trim() !== '' && draft.valueJson.trim() !== ''
    && (draft.continuity !== 'period' || (draft.effectiveFrom !== '' && draft.effectiveTo !== ''));

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-sub">
        時間・場所・業務の配置制約と通勤情報を登録・確認します。単発の希望休はここでは扱いません (シフト希望からの提出をご利用ください)。
      </p>

      <div className="max-w-xs">
        <label className="block text-xs text-text-sub mb-1">対象者</label>
        {employeesLoading ? (
          <div className="text-xs text-text-sub">読み込み中…</div>
        ) : employeesError || employees.length === 0 ? (
          <p className="text-xs text-text-sub">対象者を検索できません (自分自身の配置制約のみ表示します)。</p>
        ) : (
          <Select value={selectedEmployeeId || '__unselected__'} onValueChange={(v) => setSelectedEmployeeId(v === '__unselected__' ? '' : v)}>
            <SelectTrigger aria-label="対象者"><SelectValue placeholder="選択してください" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__unselected__">選択してください</SelectItem>
              {employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{employeeLabel(e)}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {effectiveEmployeeId && (
        <div className="border border-surface-border rounded-lg bg-white p-3 space-y-2">
          <h3 className="text-sm font-semibold text-text-body">通勤情報</h3>
          <div className="max-w-xs">
            <Select value={profile?.commute_type ?? '__none__'} onValueChange={(v) => profileMut.mutate(v === '__none__' ? null : v)}>
              <SelectTrigger aria-label="通勤種別"><SelectValue placeholder="未設定" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">未設定</SelectItem>
                <SelectItem value="train">電車</SelectItem>
                <SelectItem value="bicycle">自転車</SelectItem>
                <SelectItem value="motorcycle">バイク</SelectItem>
                <SelectItem value="car">車</SelectItem>
                <SelectItem value="walk">徒歩</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-text-body">制約一覧</h2>
        <Button variant="secondary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setDraft(emptyDraft)} disabled={!shouldQuery}>
          制約を追加
        </Button>
      </div>

      {!shouldQuery ? (
        <p className="text-sm text-text-sub">上の「対象者」からまず選択してください。</p>
      ) : isLoading ? (
        <div className="text-sm text-text-sub">読み込み中…</div>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">
          {error instanceof ApiClientError && error.code === 'PERMISSION_DENIED'
            ? 'この対象者の配置制約を閲覧する権限がありません。'
            : '配置制約の取得に失敗しました。'}
        </p>
      ) : constraints.length === 0 ? (
        <EmptyState message="配置制約がまだ登録されていません" />
      ) : (
        <ul className="space-y-2">
          {constraints.map((c) => (
            <li key={c.constraint_id} className="border border-surface-border rounded-lg bg-white p-3">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-text-body break-words">{c.sub_dimension} / {c.detail_key}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-misespark-navy/10 text-misespark-navy whitespace-nowrap">
                      {DIMENSION_LABELS[c.dimension]}
                    </span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub whitespace-nowrap">
                      {CONTINUITY_LABELS[c.continuity]}
                    </span>
                    <span className={`text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${JUDGMENT_BADGE_CLASS[c.judgment]}`}>
                      {JUDGMENT_LABELS[c.judgment]}
                    </span>
                  </div>
                  <p className="text-xs text-text-sub break-words">{c.value_json}</p>
                  <p className="text-[11px] text-text-sub">解除手段: {RELEASE_METHOD_LABELS[c.release_method]}</p>
                  {c.continuity === 'period' && (
                    <p className="text-[11px] text-text-sub">期間: {c.effective_from} 〜 {c.effective_to}</p>
                  )}
                  {c.review_due_at && (
                    <p className="text-[11px] text-text-sub">次回棚卸し: {c.review_due_at.slice(0, 10)}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button type="button" aria-label="判定を確定" onClick={() => setJudgmentTarget(c)}
                    className="inline-flex items-center gap-1 text-[11px] text-misespark-navy underline underline-offset-2 min-h-[44px] px-2">
                    <CheckCircle2 size={12} aria-hidden />確定
                  </button>
                  <button type="button" aria-label="この制約を削除" onClick={() => setDeleteTarget(c)}
                    className="w-11 h-11 shrink-0 inline-flex items-center justify-center rounded text-text-sub hover:text-danger-text focus-ring">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!draft} title="配置制約を追加" onClose={() => setDraft(null)} size="md"
        footer={draft && (
          <>
            <Button variant="secondary" onClick={() => setDraft(null)}>キャンセル</Button>
            <Button variant="primary" disabled={!canSaveDraft || createMut.isPending} loading={createMut.isPending}
              onClick={() => draft && createMut.mutate(draft)}>登録</Button>
          </>
        )}>
        {draft && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-text-sub mb-1">制限次元</label>
              <Select value={draft.dimension} onValueChange={(v) => setDraft({ ...draft, dimension: v as Constraint['dimension'] })}>
                <SelectTrigger aria-label="制限次元"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="time">時間</SelectItem>
                  <SelectItem value="place">場所</SelectItem>
                  <SelectItem value="task">業務</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">区分 (例: no_night_shift)</label>
              <input value={draft.subDimension} onChange={(e) => setDraft({ ...draft, subDimension: e.target.value })} maxLength={100}
                className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">詳細キー (例: after_22)</label>
              <input value={draft.detailKey} onChange={(e) => setDraft({ ...draft, detailKey: e.target.value })} maxLength={100}
                className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">内容 (自由記述、例: 22時以降は不可)</label>
              <textarea value={draft.valueJson} onChange={(e) => setDraft({ ...draft, valueJson: e.target.value })} maxLength={2000} rows={2}
                className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-text-sub mb-1">継続性</label>
              <Select value={draft.continuity} onValueChange={(v) => setDraft({ ...draft, continuity: v as Constraint['continuity'] })}>
                <SelectTrigger aria-label="継続性"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="permanent">恒久</SelectItem>
                  <SelectItem value="period">期間限定</SelectItem>
                  <SelectItem value="recurring">定期</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {draft.continuity === 'period' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm text-text-sub mb-1">開始日</label>
                  <input type="date" value={draft.effectiveFrom} onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })}
                    className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
                </div>
                <div>
                  <label className="block text-sm text-text-sub mb-1">終了日</label>
                  <input type="date" value={draft.effectiveTo} onChange={(e) => setDraft({ ...draft, effectiveTo: e.target.value })}
                    className="w-full px-3 py-2 rounded border border-surface-border focus-ring" />
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm text-text-sub mb-1">解除手段</label>
              <Select value={draft.releaseMethod} onValueChange={(v) => setDraft({ ...draft, releaseMethod: v as Constraint['release_method'] })}>
                <SelectTrigger aria-label="解除手段"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  <SelectItem value="money">金銭</SelectItem>
                  <SelectItem value="ops">運用調整</SelectItem>
                  <SelectItem value="consent">本人同意</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!judgmentTarget} title="判定を確定" onClose={() => setJudgmentTarget(null)} size="sm"
        footer={judgmentTarget && (
          <Button variant="secondary" onClick={() => setJudgmentTarget(null)}>閉じる</Button>
        )}>
        {judgmentTarget && (
          <div className="space-y-2">
            <p className="text-sm text-text-sub">「{judgmentTarget.sub_dimension} / {judgmentTarget.detail_key}」の判定を選択してください。</p>
            {(['ok', 'conditional', 'ng'] as const).map((j) => (
              <Button key={j} variant={judgmentTarget.judgment === j ? 'primary' : 'secondary'} className="w-full justify-start"
                disabled={judgmentMut.isPending}
                onClick={() => judgmentMut.mutate({ constraint: judgmentTarget, judgment: j })}>
                {JUDGMENT_LABELS[j]}
              </Button>
            ))}
          </div>
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTarget} title="配置制約を削除"
        message={`「${deleteTarget?.sub_dimension} / ${deleteTarget?.detail_key}」を削除します。この操作は取り消せません。`}
        confirmLabel="削除する" destructive loading={deleteMut.isPending}
        onClose={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.constraint_id)} />
    </div>
  );
}

// =============================================================================
// SC-08 配置可否マトリクス (人・組織機能 増分⑤、D-607/D-636/D-647/D-648)
// =============================================================================

type MatrixPerson = { person_key: string; display_name: string };
type MatrixCell = { person_key: string; node_id: string; judgment: Constraint['judgment']; needs_review: boolean };
type PlacementMatrixResponse = { node_ids: string[]; people: MatrixPerson[]; cells: MatrixCell[] };

/** D-648: 四値は色だけでなく記号+文言も併記する (色覚多様性対応)。既存 JUDGMENT_BADGE_CLASS/JUDGMENT_LABELS
 *  (success/alert/danger/surface-sunken トークン) をそのまま流用する — この4値は §6-2 の色トークン表と一致する。 */
const JUDGMENT_SYMBOLS: Record<Constraint['judgment'], string> = { ok: '✓', conditional: '△', ng: '✕', unconfirmed: '?' };

function JudgmentBadge({ judgment, needsReview }: { judgment: Constraint['judgment']; needsReview?: boolean }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded border whitespace-nowrap ${JUDGMENT_BADGE_CLASS[judgment]}`}>
        <span aria-hidden>{JUDGMENT_SYMBOLS[judgment]}</span>{JUDGMENT_LABELS[judgment]}
      </span>
      {needsReview && <span className="text-[10px] text-alert-text whitespace-nowrap">要棚卸し</span>}
    </div>
  );
}

function PlacementMatrixTab() {
  const user = useAuthStore((s) => s.user);
  const [deselectedIds, setDeselectedIds] = useState<Set<string>>(new Set());
  const [mobilePersonKey, setMobilePersonKey] = useState('');

  const { data: nodesData, isLoading: nodesLoading, isError: nodesError } = useQuery<NodesResponse>({
    queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes'),
  });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  // D-636と同じ理由 (isResourceInPermissionScope はself scopeの分岐を持たない、CompetencyMatrixPage.tsx
  // と同一判断): own_store/node_scope ユーザーは user.stores に含まれるノードだけを選択肢にする。
  const selectableNodes = useMemo(() => (
    user?.stores && user.stores.length > 0
      ? flatNodes.filter((n) => user.stores!.includes(n.node.node_id))
      : flatNodes
  ), [flatNodes, user?.stores]);
  const selectedNodeIds = useMemo(
    () => selectableNodes.map((n) => n.node.node_id).filter((id) => !deselectedIds.has(id)),
    [selectableNodes, deselectedIds],
  );
  const toggleNode = (nodeId: string) => setDeselectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
    return next;
  });
  const nodeName = (nodeId: string) => selectableNodes.find((n) => n.node.node_id === nodeId)?.node.name ?? nodeId;

  const nodeIdsParam = selectedNodeIds.join(',');
  const { data, isLoading, isError, error } = useQuery<PlacementMatrixResponse>({
    queryKey: ['placement-matrix', nodeIdsParam],
    queryFn: () => apiFetch<PlacementMatrixResponse>(`/placement/matrix?node_ids=${encodeURIComponent(nodeIdsParam)}`),
    enabled: selectedNodeIds.length > 0,
  });
  const people = data?.people ?? [];
  const cellByKey = new Map((data?.cells ?? []).map((c) => [`${c.person_key}|${c.node_id}`, c]));

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-sub">
        人×店舗の配置可否を一覧します。四値 (可・条件付き可・不可・未確認) は色だけでなく記号・文言でも区別しています。
      </p>

      {nodesLoading ? (
        <div role="status" className="text-text-sub">読み込み中…</div>
      ) : nodesError ? (
        <p role="alert" className="text-sm text-alert-text">組織情報の取得に失敗しました。</p>
      ) : selectableNodes.length === 0 ? (
        <EmptyState message="店舗が見つかりません" description="組織階層が設定されていないか、閲覧できる店舗がありません。" />
      ) : (
        <>
          <div>
            <div className="text-xs text-text-sub mb-1.5">対象店舗</div>
            <div className="flex flex-wrap gap-2">
              {selectableNodes.map(({ node }) => (
                <label key={node.node_id}
                  className="inline-flex items-center gap-1.5 text-xs border border-surface-border rounded-full px-2.5 min-h-[32px] cursor-pointer has-[:checked]:border-chrome-base has-[:checked]:bg-chrome-base/5">
                  <input type="checkbox" className="w-4 h-4 accent-chrome-base" checked={!deselectedIds.has(node.node_id)}
                    onChange={() => toggleNode(node.node_id)} aria-label={`${node.name} を対象に含める`} />
                  {node.name}
                </label>
              ))}
            </div>
          </div>

          {selectedNodeIds.length === 0 ? (
            <p className="text-sm text-text-sub">上の「対象店舗」から少なくとも1店舗を選択してください。</p>
          ) : isLoading ? (
            <div role="status" className="text-text-sub">読み込み中…</div>
          ) : isError ? (
            <p role="alert" className="text-sm text-alert-text">
              {error instanceof ApiClientError && error.code === 'PERMISSION_DENIED'
                ? '配置可否マトリクスを閲覧する権限がありません。'
                : 'マトリクスの取得に失敗しました。'}
            </p>
          ) : people.length === 0 ? (
            <EmptyState message="対象となる人がいません" description="選択した店舗にまだ誰も在籍していません。" />
          ) : (
            <>
              {/* ≥768px: 人×店舗のグリッド (D-647) */}
              <div className="hidden md:block border border-surface-border rounded-lg overflow-hidden bg-white">
                <div className="overflow-x-auto">
                  <table className="border-collapse text-sm">
                    <thead>
                      <tr>
                        <th scope="col" className="sticky left-0 z-10 bg-surface-sunken border-b border-r border-surface-border px-3 py-2 text-left text-xs text-text-sub" style={{ minWidth: 160 }}>
                          対象者
                        </th>
                        {selectedNodeIds.map((nodeId) => (
                          <th key={nodeId} scope="col" className="relative border-b border-surface-border px-2 py-2 text-center text-xs text-text-sub whitespace-nowrap" style={{ minWidth: 120 }}>
                            {nodeName(nodeId)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {people.map((p) => (
                        <tr key={p.person_key} className="odd:bg-white even:bg-surface-stripe">
                          <th scope="row" className="sticky left-0 z-10 bg-inherit border-r border-surface-border px-3 py-2 text-left align-middle font-semibold text-text-body break-words">
                            {p.display_name}
                          </th>
                          {selectedNodeIds.map((nodeId) => {
                            const cell = cellByKey.get(`${p.person_key}|${nodeId}`);
                            return (
                              <td key={nodeId} className="px-2 py-2 text-center align-middle">
                                {cell ? <JudgmentBadge judgment={cell.judgment} needsReview={cell.needs_review} /> : <span className="text-xs text-text-sub">—</span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* <768px: 対象者を選択してドリルダウン (D-647) */}
              <div className="md:hidden space-y-2">
                <div className="max-w-xs">
                  <label className="block text-xs text-text-sub mb-1" htmlFor="matrix-person-mobile">対象者で絞り込み</label>
                  <Select value={mobilePersonKey || '__none__'} onValueChange={(v) => setMobilePersonKey(v === '__none__' ? '' : v)}>
                    <SelectTrigger id="matrix-person-mobile" aria-label="対象者で絞り込み"><SelectValue placeholder="選択してください" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">選択してください</SelectItem>
                      {people.map((p) => <SelectItem key={p.person_key} value={p.person_key}>{p.display_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {!mobilePersonKey ? (
                  <p className="text-sm text-text-sub">上の「対象者で絞り込み」からまず選択してください。</p>
                ) : (
                  <ul className="space-y-2">
                    {selectedNodeIds.map((nodeId) => {
                      const cell = cellByKey.get(`${mobilePersonKey}|${nodeId}`);
                      return (
                        <li key={nodeId} className="border border-surface-border rounded-lg bg-white p-3 flex items-center justify-between gap-2">
                          <span className="text-sm text-text-body break-words">{nodeName(nodeId)}</span>
                          {cell ? <JudgmentBadge judgment={cell.judgment} needsReview={cell.needs_review} /> : <span className="text-xs text-text-sub">—</span>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

export function PlacementConstraintsPage() {
  const [tab, setTab] = useState<'constraints' | 'matrix'>('constraints');
  const tabs: ReadonlyArray<readonly [typeof tab, string]> = [
    ['constraints', '配置制約'],
    ['matrix', '配置可否マトリクス'],
  ] as const;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-page-title text-text-main">配置制約</h1>
      </div>

      <div className="border-b border-surface-border overflow-x-auto">
        <nav className="flex gap-1 min-w-max" aria-label="タブ">
          {tabs.map(([key, label]) => (
            <button key={key} type="button" onClick={() => setTab(key)} aria-current={tab === key}
              className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 min-h-[44px] transition ${
                tab === key ? 'border-brand-red text-brand-red font-semibold' : 'border-transparent text-text-sub hover:text-text-body hover:border-surface-border'
              }`}>
              {label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'constraints' ? <ConstraintsTab /> : <PlacementMatrixTab />}
    </div>
  );
}
